import React, { useState } from 'react';

import { CheckCircleFilled, CloseCircleFilled, DownOutlined, ThunderboltFilled, UpOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { EMAIL_MATCHING_IDENTITY_SIGNAL_MISMATCH_NOTES } from './constants';
import { EmailMatchingIdentitySignalType } from './types';

type Props = {
  communityName: string | null | undefined;
  confidence: string | null;
  signals: EmailMatchingIdentitySignalType[];
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--line-gray-bold);
  border-radius: 8px;
  background: var(--feather-blue);

  &.expandable {
    cursor: pointer;

    &:hover {
      background: var(--faded-blue);
      border-color: var(--blue-sky);
    }
  }
`;

const BannerIcon = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--button-blue);
  color: var(--white);
  font-size: 16px;
`;

const BannerTexts = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BannerTitle = styled.span`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--deep-blue);
`;

const BannerSubtitle = styled.span`
  font-size: 12px;
  color: var(--dark-blue);
`;

const BannerNote = styled.span`
  font-size: 12px;
  font-family: var(--font-regular);

  &.matched {
    color: var(--positive-green-dark);
  }

  &.unmatched {
    color: var(--dark-orange);
  }
`;

const BannerChevron = styled.span`
  flex: 0 0 auto;
  font-size: 16px;
  color: var(--dark-blue);
`;

const SignalsPanel = styled.div`
  margin: 0 6px;
  padding: 16px 16px 14px;
  border: 1px solid var(--line-gray-bold);
  border-top: none;
  border-radius: 0 0 8px 8px;
  background: var(--white);
`;

const SignalsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const SignalsTitle = styled.span`
  font-size: 13px;
  font-family: var(--font-bold);
`;

const ConfidenceBadge = styled.span`
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 12px;
  font-family: var(--font-bold);

  &.warning {
    background: var(--peach-pale);
    color: var(--communication-channel-sms-dark);
  }

  &.danger {
    background: var(--error-red-background);
    color: var(--error-red-dark);
  }
`;

const SignalsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SignalRow = styled.div`
  display: flex;
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--lighter-gray);
`;

const SignalIcon = styled.span`
  flex: 0 0 auto;
  margin-top: 1px;
  font-size: 16px;

  &.matched {
    color: var(--positive-green);
  }

  &.unmatched {
    color: var(--dark-orange);
  }
`;

const SignalTexts = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const SignalLabel = styled.span`
  font-size: 13px;
  font-family: var(--font-bold);
`;

const SignalEmailValue = styled.span`
  margin-top: 2px;
  font-size: 12px;
  color: var(--text-secondary);
`;

const SignalFurtherValue = styled.span`
  margin-top: 1px;
  font-size: 12px;
  font-family: var(--font-regular);
`;

const SignalNote = styled.span`
  margin-top: 3px;
  font-size: 12px;
  font-family: var(--font-regular);
  color: var(--dark-orange);
`;

const EmailMatchingAiSuggestion = ({ communityName, confidence, signals }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasSignals = signals.length > 0;
  const unmatchedCount = signals.filter((signal) => !signal.matches).length;

  const subtitleParts = [`Pre-filled below${confidence ? ` (${confidence} confidence)` : ''}`];
  if (hasSignals) {
    subtitleParts.push(isExpanded ? 'Hide identity signals' : 'See why');
  }

  const unmatchedNote =
    unmatchedCount === 1
      ? '1 signal doesn’t match — review before confirming'
      : `${unmatchedCount} signals don’t match — review before confirming`;

  return (
    <Container>
      <Banner
        data-test-id="email-matching-detail-ai-suggestion-banner"
        className={hasSignals ? 'expandable' : undefined}
        role={hasSignals ? 'button' : undefined}
        tabIndex={hasSignals ? 0 : undefined}
        onClick={hasSignals ? () => setIsExpanded((value) => !value) : undefined}
      >
        <BannerIcon>
          <ThunderboltFilled />
        </BannerIcon>
        <BannerTexts>
          <BannerTitle>AI suggests {communityName}</BannerTitle>
          <BannerSubtitle>{subtitleParts.join(' · ')}</BannerSubtitle>
          {hasSignals && (
            <BannerNote
              data-test-id="email-matching-detail-identity-signals-note"
              className={unmatchedCount ? 'unmatched' : 'matched'}
            >
              {unmatchedCount ? unmatchedNote : 'All identity signals match'}
            </BannerNote>
          )}
        </BannerTexts>
        {hasSignals && <BannerChevron>{isExpanded ? <UpOutlined /> : <DownOutlined />}</BannerChevron>}
      </Banner>

      {hasSignals && isExpanded && (
        <SignalsPanel data-test-id="email-matching-detail-identity-signals">
          <SignalsHeader>
            <SignalsTitle>Why this match</SignalsTitle>
            {confidence && (
              <ConfidenceBadge className={unmatchedCount ? 'danger' : 'warning'}>
                {confidence} confidence
              </ConfidenceBadge>
            )}
          </SignalsHeader>
          <SignalsList>
            {signals.map((signal) => (
              <SignalRow key={signal.key} data-test-id={`email-matching-detail-identity-signal-${signal.key}`}>
                <SignalIcon className={signal.matches ? 'matched' : 'unmatched'}>
                  {signal.matches ? <CheckCircleFilled /> : <CloseCircleFilled />}
                </SignalIcon>
                <SignalTexts>
                  <SignalLabel>{signal.label}</SignalLabel>
                  <SignalEmailValue>In email: {signal.email_value}</SignalEmailValue>
                  <SignalFurtherValue>In Further: {signal.community_value}</SignalFurtherValue>
                  {!signal.matches && EMAIL_MATCHING_IDENTITY_SIGNAL_MISMATCH_NOTES[signal.key] && (
                    <SignalNote>{EMAIL_MATCHING_IDENTITY_SIGNAL_MISMATCH_NOTES[signal.key]}</SignalNote>
                  )}
                </SignalTexts>
              </SignalRow>
            ))}
          </SignalsList>
        </SignalsPanel>
      )}
    </Container>
  );
};

export default EmailMatchingAiSuggestion;
